import { supabase } from '../lib/supabase';

// ============================================================
// FIXED EXPENSES
// ============================================================
const fromFixedExpenseRow = (row) => ({
  id: row?.id,
  name: row?.name,
  category: row?.category,
  monthlyCost: parseFloat(row?.monthly_cost) || 0,
  description: row?.description || '',
  groupName: row?.group_name || null,
  userId: row?.user_id,
  isDeleted: row?.is_deleted || false,
  deletedAt: row?.deleted_at || null,
  deletedByEmail: row?.deleted_by_email || null,
});

const toFixedExpenseRow = (data, userId) => ({
  user_id: userId,
  name: data?.name,
  category: data?.category,
  monthly_cost: parseFloat(data?.monthlyCost) || 0,
  description: data?.description || '',
  group_name: data?.groupName || null,
});

export const getFixedExpenses = async () => {
  const { data, error } = await supabase?.from('fixed_expenses')?.select('*')?.eq('is_deleted', false)?.order('created_at', { ascending: true });
  if (error) throw error;
  return (data || [])?.map(fromFixedExpenseRow);
};

export const getDeletedFixedExpenses = async () => {
  const { data, error } = await supabase?.from('fixed_expenses')?.select('*')?.eq('is_deleted', true)?.order('deleted_at', { ascending: false });
  if (error) throw error;
  return (data || [])?.map(fromFixedExpenseRow);
};

export const createFixedExpense = async (item, userId) => {
  const { data, error } = await supabase?.from('fixed_expenses')?.insert(toFixedExpenseRow(item, userId))?.select()?.single();
  if (error) throw error;
  return fromFixedExpenseRow(data);
};

export const updateFixedExpense = async (id, item) => {
  const { data, error } = await supabase?.from('fixed_expenses')?.update({
    name: item?.name,
    category: item?.category,
    monthly_cost: parseFloat(item?.monthlyCost) || 0,
    description: item?.description || '',
    group_name: item?.groupName !== undefined ? (item?.groupName || null) : undefined,
    updated_at: new Date()?.toISOString(),
  })?.eq('id', id)?.select()?.single();
  if (error) throw error;
  return fromFixedExpenseRow(data);
};

export const deleteFixedExpense = async (id, userEmail) => {
  const { error } = await supabase?.from('fixed_expenses')?.update({
    is_deleted: true,
    deleted_at: new Date()?.toISOString(),
    deleted_by_email: userEmail || null,
  })?.eq('id', id);
  if (error) throw error;
  return true;
};

export const restoreFixedExpense = async (id) => {
  const { data, error } = await supabase?.from('fixed_expenses')?.update({
    is_deleted: false,
    deleted_at: null,
    deleted_by_email: null,
    updated_at: new Date()?.toISOString(),
  })?.eq('id', id)?.select()?.single();
  if (error) throw error;
  return fromFixedExpenseRow(data);
};

export const permanentlyDeleteFixedExpense = async (id) => {
  const { error } = await supabase?.from('fixed_expenses')?.delete()?.eq('id', id);
  if (error) throw error;
  return true;
};

// ============================================================
// EXPENSE GROUPS
// ============================================================
const fromExpenseGroupRow = (row) => ({
  id: row?.id,
  name: row?.name,
  description: row?.description || '',
  userId: row?.user_id,
  createdAt: row?.created_at,
});

export const getExpenseGroups = async () => {
  const { data, error } = await supabase
    ?.from('expense_groups')
    ?.select('*')
    ?.order('name', { ascending: true });
  if (error) throw error;
  return (data || [])?.map(fromExpenseGroupRow);
};

export const createExpenseGroup = async (group, userId) => {
  const { data, error } = await supabase
    ?.from('expense_groups')
    ?.insert({ user_id: userId, name: group?.name?.trim(), description: group?.description || '' })
    ?.select()
    ?.single();
  if (error) throw error;
  return fromExpenseGroupRow(data);
};

export const deleteExpenseGroup = async (id) => {
  const { error } = await supabase?.from('expense_groups')?.delete()?.eq('id', id);
  if (error) throw error;
  return true;
};

// ============================================================
// EMPLOYEES FROM SALARIES GROUP
// If a "Salaries" expense group exists, treat all child fixed
// expense records under that group as employees.
// Returns an array shaped like the employee pool objects used
// by fetchEmployees / MultiSelectEmployee.
// ============================================================
export const getEmployeesFromSalariesGroup = async () => {
  try {
    // 1. Find the "Salaries" group (case-insensitive)
    const { data: groups, error: groupErr } = await supabase
      ?.from('expense_groups')
      ?.select('id, name')
      ?.ilike('name', 'salaries');
    if (groupErr) throw groupErr;
    if (!groups || groups?.length === 0) return [];

    const salariesGroupName = groups?.[0]?.name;

    // 2. Fetch all non-deleted fixed expenses assigned to that group (by name)
    const { data: items, error: itemsErr } = await supabase
      ?.from('fixed_expenses')
      ?.select('id, name, category, monthly_cost, description')
      ?.eq('is_deleted', false)
      ?.ilike('group_name', salariesGroupName);
    if (itemsErr) throw itemsErr;

    // 3. Map to employee-shaped objects
    return (items || [])?.map(item => ({
      id: `salary-${item?.id}`,
      employeeCode: null,
      fullName: item?.name || 'Unknown',
      department: item?.category || 'Salaries',
      role: item?.category || 'Employee',
      email: null,
      phone: null,
      isActive: true,
      monthlySalary: parseFloat(item?.monthly_cost) || 0,
      source: 'salaries_group',
    }));
  } catch (err) {
    console.warn('[getEmployeesFromSalariesGroup] Error:', err?.message);
    return [];
  }
};

// ============================================================
// VARIABLE EXPENSES
// ============================================================
const fromVariableExpenseRow = (row) => ({
  id: row?.id,
  name: row?.name,
  category: row?.category,
  monthlyCost: parseFloat(row?.monthly_cost) || 0,
  description: row?.description || '',
  userId: row?.user_id,
  isDeleted: row?.is_deleted || false,
  deletedAt: row?.deleted_at || null,
  deletedByEmail: row?.deleted_by_email || null,
});

const toVariableExpenseRow = (data, userId) => ({
  user_id: userId,
  name: data?.name,
  category: data?.category,
  monthly_cost: parseFloat(data?.monthlyCost) || 0,
  description: data?.description || '',
});

export const getVariableExpenses = async () => {
  const { data, error } = await supabase?.from('variable_expenses')?.select('*')?.eq('is_deleted', false)?.order('created_at', { ascending: true });
  if (error) throw error;
  return (data || [])?.map(fromVariableExpenseRow);
};

export const getDeletedVariableExpenses = async () => {
  const { data, error } = await supabase?.from('variable_expenses')?.select('*')?.eq('is_deleted', true)?.order('deleted_at', { ascending: false });
  if (error) throw error;
  return (data || [])?.map(fromVariableExpenseRow);
};

export const createVariableExpense = async (item, userId) => {
  const { data, error } = await supabase?.from('variable_expenses')?.insert(toVariableExpenseRow(item, userId))?.select()?.single();
  if (error) throw error;
  return fromVariableExpenseRow(data);
};

export const updateVariableExpense = async (id, item) => {
  const { data, error } = await supabase?.from('variable_expenses')?.update({
    name: item?.name,
    category: item?.category,
    monthly_cost: parseFloat(item?.monthlyCost) || 0,
    description: item?.description || '',
    updated_at: new Date()?.toISOString(),
  })?.eq('id', id)?.select()?.single();
  if (error) throw error;
  return fromVariableExpenseRow(data);
};

export const deleteVariableExpense = async (id, userEmail) => {
  const { error } = await supabase?.from('variable_expenses')?.update({
    is_deleted: true,
    deleted_at: new Date()?.toISOString(),
    deleted_by_email: userEmail || null,
  })?.eq('id', id);
  if (error) throw error;
  return true;
};

export const restoreVariableExpense = async (id) => {
  const { data, error } = await supabase?.from('variable_expenses')?.update({
    is_deleted: false,
    deleted_at: null,
    deleted_by_email: null,
    updated_at: new Date()?.toISOString(),
  })?.eq('id', id)?.select()?.single();
  if (error) throw error;
  return fromVariableExpenseRow(data);
};

export const permanentlyDeleteVariableExpense = async (id) => {
  const { error } = await supabase?.from('variable_expenses')?.delete()?.eq('id', id);
  if (error) throw error;
  return true;
};

// ============================================================
// MATERIALS
// ============================================================
const fromMaterialRow = (row) => ({
  id: row?.id,
  itemCode: row?.item_code,
  itemName: row?.item_name,
  category: row?.category,
  uom: row?.uom,
  unitCost: parseFloat(row?.unit_cost) || 0,
  wastePercentage: parseFloat(row?.waste_percentage) || 0,
  currency: row?.currency || 'OMR',
  unitCostOriginal: parseFloat(row?.unit_cost_original) || parseFloat(row?.unit_cost) || 0,
  unitCostOmr: parseFloat(row?.unit_cost_omr) || parseFloat(row?.unit_cost) || 0,
  exchangeRate: parseFloat(row?.exchange_rate) || 1,
  exchangeRateDate: row?.exchange_rate_date || new Date()?.toISOString()?.split('T')?.[0],
  userId: row?.user_id,
  isDeleted: row?.is_deleted || false,
  deletedAt: row?.deleted_at || null,
  deletedByEmail: row?.deleted_by_email || null,
});

const toMaterialRow = (data, userId) => ({
  user_id: userId,
  item_code: data?.itemCode,
  item_name: data?.itemName,
  category: data?.category,
  uom: data?.uom,
  unit_cost: parseFloat(data?.unitCostOmr ?? data?.unitCost) || 0,
  waste_percentage: parseFloat(data?.wastePercentage) || 0,
  currency: data?.currency || 'OMR',
  unit_cost_original: parseFloat(data?.unitCostOriginal ?? data?.unitCost) || 0,
  unit_cost_omr: parseFloat(data?.unitCostOmr ?? data?.unitCost) || 0,
  exchange_rate: parseFloat(data?.exchangeRate) || 1,
  exchange_rate_date: data?.exchangeRateDate || new Date()?.toISOString()?.split('T')?.[0],
});

export const getMaterials = async () => {
  const { data, error } = await supabase?.from('materials')?.select('*')?.eq('is_deleted', false)?.order('created_at', { ascending: true });
  if (error) throw error;
  return (data || [])?.map(fromMaterialRow);
};

export const getDeletedMaterials = async () => {
  const { data, error } = await supabase?.from('materials')?.select('*')?.eq('is_deleted', true)?.order('deleted_at', { ascending: false });
  if (error) throw error;
  return (data || [])?.map(fromMaterialRow);
};

export const createMaterial = async (item, userId) => {
  const { data, error } = await supabase?.from('materials')?.insert(toMaterialRow(item, userId))?.select()?.single();
  if (error) throw error;
  return fromMaterialRow(data);
};

export const updateMaterial = async (id, item) => {
  const { data, error } = await supabase?.from('materials')?.update({
    item_code: item?.itemCode,
    item_name: item?.itemName,
    category: item?.category,
    uom: item?.uom,
    unit_cost: parseFloat(item?.unitCostOmr ?? item?.unitCost) || 0,
    waste_percentage: parseFloat(item?.wastePercentage) || 0,
    currency: item?.currency || 'OMR',
    unit_cost_original: parseFloat(item?.unitCostOriginal ?? item?.unitCost) || 0,
    unit_cost_omr: parseFloat(item?.unitCostOmr ?? item?.unitCost) || 0,
    exchange_rate: parseFloat(item?.exchangeRate) || 1,
    exchange_rate_date: item?.exchangeRateDate || new Date()?.toISOString()?.split('T')?.[0],
    updated_at: new Date()?.toISOString(),
  })?.eq('id', id)?.select()?.single();
  if (error) throw error;
  return fromMaterialRow(data);
};

export const deleteMaterial = async (id, userEmail) => {
  const { error } = await supabase?.from('materials')?.update({
    is_deleted: true,
    deleted_at: new Date()?.toISOString(),
    deleted_by_email: userEmail || null,
  })?.eq('id', id);
  if (error) throw error;
  return true;
};

export const restoreMaterial = async (id) => {
  const { data, error } = await supabase?.from('materials')?.update({
    is_deleted: false,
    deleted_at: null,
    deleted_by_email: null,
    updated_at: new Date()?.toISOString(),
  })?.eq('id', id)?.select()?.single();
  if (error) throw error;
  return fromMaterialRow(data);
};

export const permanentlyDeleteMaterial = async (id) => {
  const { error } = await supabase?.from('materials')?.delete()?.eq('id', id);
  if (error) throw error;
  return true;
};

// ============================================================
// ENGINEERS
// ============================================================
const WORKING_DAYS_PER_MONTH = 30;
const WORKING_HOURS_PER_DAY = 9;

const fromEngineerRow = (row) => {
  const monthlySalary = parseFloat(row?.monthly_salary) || 0;
  const dailySalary = monthlySalary > 0
    ? parseFloat((monthlySalary / WORKING_DAYS_PER_MONTH)?.toFixed(6))
    : parseFloat(row?.daily_salary) || 0;
  const hourlySalary = dailySalary > 0
    ? parseFloat((dailySalary / WORKING_HOURS_PER_DAY)?.toFixed(6))
    : parseFloat(row?.hourly_salary) || 0;
  const nationality = row?.nationality || 'expat';
  const spfPercentage = parseFloat(row?.spf_percentage) || 22.5;
  const spfValue = nationality === 'omani' ? monthlySalary * (spfPercentage / 100) : 0;
  return {
    id: row?.id,
    name: row?.name,
    role: row?.role,
    specialization: row?.specialization,
    monthlySalary,
    dailySalary,
    hourlySalary,
    internalCost: parseFloat(row?.internal_cost) || dailySalary,
    utilizationDefault: parseFloat(row?.utilization_default) || 85,
    nationality,
    spfPercentage,
    spfValue,
    healthInsuranceEnabled: row?.health_insurance_enabled ?? false,
    healthInsuranceValue: parseFloat(row?.health_insurance_value) || 0,
    userId: row?.user_id,
    isDeleted: row?.is_deleted || false,
    deletedAt: row?.deleted_at || null,
    deletedByEmail: row?.deleted_by_email || null,
  };
};

const toEngineerRow = (data, userId) => {
  const monthly = parseFloat(data?.monthlySalary) || 0;
  const daily = monthly > 0 ? parseFloat((monthly / WORKING_DAYS_PER_MONTH)?.toFixed(6)) : parseFloat(data?.dailySalary) || 0;
  const hourly = daily > 0 ? parseFloat((daily / WORKING_HOURS_PER_DAY)?.toFixed(6)) : parseFloat(data?.hourlySalary) || 0;
  const nationality = data?.nationality || 'expat';
  const spfPercentage = parseFloat(data?.spfPercentage) || 22.5;
  return {
    user_id: userId,
    name: data?.name,
    role: data?.role,
    specialization: data?.specialization,
    monthly_salary: monthly,
    daily_salary: daily,
    hourly_salary: hourly,
    internal_cost: daily,
    utilization_default: parseFloat(data?.utilizationDefault) || 85,
    nationality,
    spf_percentage: spfPercentage,
    health_insurance_enabled: data?.healthInsuranceEnabled ?? false,
    health_insurance_value: parseFloat(data?.healthInsuranceValue) || 0,
  };
};

export const getEngineers = async () => {
  const { data, error } = await supabase?.from('engineers')?.select('*')?.eq('is_deleted', false)?.order('created_at', { ascending: true });
  if (error) throw error;
  return (data || [])?.map(fromEngineerRow);
};

export const getDeletedEngineers = async () => {
  const { data, error } = await supabase?.from('engineers')?.select('*')?.eq('is_deleted', true)?.order('deleted_at', { ascending: false });
  if (error) throw error;
  return (data || [])?.map(fromEngineerRow);
};

export const createEngineer = async (item, userId) => {
  const { data, error } = await supabase?.from('engineers')?.insert(toEngineerRow(item, userId))?.select()?.single();
  if (error) throw error;
  return fromEngineerRow(data);
};

export const updateEngineer = async (id, item) => {
  const monthly = parseFloat(item?.monthlySalary) || 0;
  const daily = monthly > 0 ? parseFloat((monthly / WORKING_DAYS_PER_MONTH)?.toFixed(6)) : parseFloat(item?.dailySalary) || 0;
  const hourly = daily > 0 ? parseFloat((daily / WORKING_HOURS_PER_DAY)?.toFixed(6)) : parseFloat(item?.hourlySalary) || 0;
  const nationality = item?.nationality || 'expat';
  const spfPercentage = parseFloat(item?.spfPercentage) || 22.5;
  const { data, error } = await supabase?.from('engineers')?.update({
    name: item?.name,
    role: item?.role,
    specialization: item?.specialization,
    monthly_salary: monthly,
    daily_salary: daily,
    hourly_salary: hourly,
    internal_cost: daily,
    utilization_default: parseFloat(item?.utilizationDefault) || 85,
    nationality,
    spf_percentage: spfPercentage,
    health_insurance_enabled: item?.healthInsuranceEnabled ?? false,
    health_insurance_value: parseFloat(item?.healthInsuranceValue) || 0,
    updated_at: new Date()?.toISOString(),
  })?.eq('id', id)?.select()?.single();
  if (error) throw error;
  return fromEngineerRow(data);
};

export const deleteEngineer = async (id, userEmail) => {
  const { error } = await supabase?.from('engineers')?.update({
    is_deleted: true,
    deleted_at: new Date()?.toISOString(),
    deleted_by_email: userEmail || null,
  })?.eq('id', id);
  if (error) throw error;
  return true;
};

export const restoreEngineer = async (id) => {
  const { data, error } = await supabase?.from('engineers')?.update({
    is_deleted: false,
    deleted_at: null,
    deleted_by_email: null,
    updated_at: new Date()?.toISOString(),
  })?.eq('id', id)?.select()?.single();
  if (error) throw error;
  return fromEngineerRow(data);
};

export const permanentlyDeleteEngineer = async (id) => {
  const { error } = await supabase?.from('engineers')?.delete()?.eq('id', id);
  if (error) throw error;
  return true;
};

// ============================================================
// SOFTWARE LICENSES
// ============================================================
const fromSoftwareLicenseRow = (row) => ({
  id: row?.id,
  softwareName: row?.software_name,
  vendor: row?.vendor,
  category: row?.category,
  licenseType: row?.license_type,
  cost: parseFloat(row?.cost) || 0,
  numberOfLicenses: parseInt(row?.number_of_licenses) || 1,
  renewalDate: row?.renewal_date || '',
  userId: row?.user_id,
  isDeleted: row?.is_deleted || false,
  deletedAt: row?.deleted_at || null,
  deletedByEmail: row?.deleted_by_email || null,
});

const toSoftwareLicenseRow = (data, userId) => ({
  user_id: userId,
  software_name: data?.softwareName,
  vendor: data?.vendor,
  category: data?.category,
  license_type: data?.licenseType,
  cost: parseFloat(data?.cost) || 0,
  number_of_licenses: parseInt(data?.numberOfLicenses) || 1,
  renewal_date: data?.renewalDate || null,
});

export const getSoftwareLicenses = async () => {
  const { data, error } = await supabase?.from('software_licenses')?.select('*')?.eq('is_deleted', false)?.order('created_at', { ascending: true });
  if (error) throw error;
  return (data || [])?.map(fromSoftwareLicenseRow);
};

export const getDeletedSoftwareLicenses = async () => {
  const { data, error } = await supabase?.from('software_licenses')?.select('*')?.eq('is_deleted', true)?.order('deleted_at', { ascending: false });
  if (error) throw error;
  return (data || [])?.map(fromSoftwareLicenseRow);
};

export const createSoftwareLicense = async (item, userId) => {
  const { data, error } = await supabase?.from('software_licenses')?.insert(toSoftwareLicenseRow(item, userId))?.select()?.single();
  if (error) throw error;
  return fromSoftwareLicenseRow(data);
};

export const updateSoftwareLicense = async (id, item) => {
  const { data, error } = await supabase?.from('software_licenses')?.update({
    software_name: item?.softwareName,
    vendor: item?.vendor,
    category: item?.category,
    license_type: item?.licenseType,
    cost: parseFloat(item?.cost) || 0,
    number_of_licenses: parseInt(item?.numberOfLicenses) || 1,
    renewal_date: item?.renewalDate || null,
    updated_at: new Date()?.toISOString(),
  })?.eq('id', id)?.select()?.single();
  if (error) throw error;
  return fromSoftwareLicenseRow(data);
};

export const deleteSoftwareLicense = async (id, userEmail) => {
  const { error } = await supabase?.from('software_licenses')?.update({
    is_deleted: true,
    deleted_at: new Date()?.toISOString(),
    deleted_by_email: userEmail || null,
  })?.eq('id', id);
  if (error) throw error;
  return true;
};

export const restoreSoftwareLicense = async (id) => {
  const { data, error } = await supabase?.from('software_licenses')?.update({
    is_deleted: false,
    deleted_at: null,
    deleted_by_email: null,
    updated_at: new Date()?.toISOString(),
  })?.eq('id', id)?.select()?.single();
  if (error) throw error;
  return fromSoftwareLicenseRow(data);
};

export const permanentlyDeleteSoftwareLicense = async (id) => {
  const { error } = await supabase?.from('software_licenses')?.delete()?.eq('id', id);
  if (error) throw error;
  return true;
};

// ============================================================
// VENDORS
// ============================================================
const fromVendorRow = (row) => ({
  id: row?.id,
  vendorName: row?.vendor_name,
  contactPerson: row?.contact_person,
  email: row?.email,
  phone: row?.phone || '',
  category: row?.category,
  rating: parseFloat(row?.rating) || 3,
  paymentTerms: row?.payment_terms || 'net30',
  userId: row?.user_id,
  isDeleted: row?.is_deleted || false,
  deletedAt: row?.deleted_at || null,
  deletedByEmail: row?.deleted_by_email || null,
});

const toVendorRow = (data, userId) => ({
  user_id: userId,
  vendor_name: data?.vendorName,
  contact_person: data?.contactPerson,
  email: data?.email,
  phone: data?.phone || '',
  category: data?.category,
  rating: parseFloat(data?.rating) || 3,
  payment_terms: data?.paymentTerms || 'net30',
});

export const getVendors = async () => {
  const { data, error } = await supabase?.from('vendors')?.select('*')?.eq('is_deleted', false)?.order('created_at', { ascending: true });
  if (error) throw error;
  return (data || [])?.map(fromVendorRow);
};

export const getDeletedVendors = async () => {
  const { data, error } = await supabase?.from('vendors')?.select('*')?.eq('is_deleted', true)?.order('deleted_at', { ascending: false });
  if (error) throw error;
  return (data || [])?.map(fromVendorRow);
};

export const createVendor = async (item, userId) => {
  const { data, error } = await supabase?.from('vendors')?.insert(toVendorRow(item, userId))?.select()?.single();
  if (error) throw error;
  return fromVendorRow(data);
};

export const updateVendor = async (id, item) => {
  const { data, error } = await supabase?.from('vendors')?.update({
    vendor_name: item?.vendorName,
    contact_person: item?.contactPerson,
    email: item?.email,
    phone: item?.phone || '',
    category: item?.category,
    rating: parseFloat(item?.rating) || 3,
    payment_terms: item?.paymentTerms || 'net30',
    updated_at: new Date()?.toISOString(),
  })?.eq('id', id)?.select()?.single();
  if (error) throw error;
  return fromVendorRow(data);
};

export const deleteVendor = async (id, userEmail) => {
  const { error } = await supabase?.from('vendors')?.update({
    is_deleted: true,
    deleted_at: new Date()?.toISOString(),
    deleted_by_email: userEmail || null,
  })?.eq('id', id);
  if (error) throw error;
  return true;
};

export const restoreVendor = async (id) => {
  const { data, error } = await supabase?.from('vendors')?.update({
    is_deleted: false,
    deleted_at: null,
    deleted_by_email: null,
    updated_at: new Date()?.toISOString(),
  })?.eq('id', id)?.select()?.single();
  if (error) throw error;
  return fromVendorRow(data);
};

export const permanentlyDeleteVendor = async (id) => {
  const { error } = await supabase?.from('vendors')?.delete()?.eq('id', id);
  if (error) throw error;
  return true;
};

// ============================================================
// OUTSOURCE RESOURCES
// ============================================================
const fromOutsourceResourceRow = (row) => {
  const monthlySalary = parseFloat(row?.monthly_salary) || 0;
  const dailySalary = monthlySalary > 0
    ? parseFloat((monthlySalary / WORKING_DAYS_PER_MONTH)?.toFixed(6))
    : parseFloat(row?.daily_salary) || 0;
  const hourlySalary = dailySalary > 0
    ? parseFloat((dailySalary / WORKING_HOURS_PER_DAY)?.toFixed(6))
    : parseFloat(row?.hourly_salary) || 0;
  const nationality = row?.nationality || 'expat';
  const spfPercentage = parseFloat(row?.spf_percentage) || 22.5;
  const spfValue = nationality === 'omani' ? parseFloat((monthlySalary * (spfPercentage / 100))?.toFixed(4)) : 0;
  return {
    id: row?.id,
    name: row?.name || row?.resource_name || '',
    role: row?.role || 'mid',
    specialization: row?.specialization || row?.role_skill || '',
    monthlySalary,
    dailySalary,
    hourlySalary,
    internalCost: parseFloat(row?.internal_cost) || dailySalary,
    utilizationDefault: parseFloat(row?.utilization_default) || 85,
    nationality,
    spfPercentage,
    spfValue,
    healthInsuranceEnabled: row?.health_insurance_enabled ?? false,
    healthInsuranceValue: parseFloat(row?.health_insurance_value) || 0,
    userId: row?.user_id,
    isDeleted: row?.is_deleted || false,
    deletedAt: row?.deleted_at || null,
    deletedByEmail: row?.deleted_by_email || null,
  };
};

const toOutsourceResourceRow = (data, userId) => {
  const monthly = parseFloat(data?.monthlySalary) || 0;
  const daily = monthly > 0 ? parseFloat((monthly / WORKING_DAYS_PER_MONTH)?.toFixed(6)) : parseFloat(data?.dailySalary) || 0;
  const hourly = daily > 0 ? parseFloat((daily / WORKING_HOURS_PER_DAY)?.toFixed(6)) : parseFloat(data?.hourlySalary) || 0;
  const nationality = data?.nationality || 'expat';
  const spfPercentage = parseFloat(data?.spfPercentage) || 22.5;
  const spfValue = nationality === 'omani' ? parseFloat((monthly * (spfPercentage / 100))?.toFixed(4)) : 0;
  return {
    user_id: userId,
    name: data?.name,
    resource_name: data?.name,
    role: data?.role || 'mid',
    specialization: data?.specialization,
    role_skill: data?.specialization,
    monthly_salary: monthly,
    daily_salary: daily,
    hourly_salary: hourly,
    internal_cost: daily,
    utilization_default: parseFloat(data?.utilizationDefault) || 85,
    nationality,
    spf_percentage: spfPercentage,
    spf_value: spfValue,
    health_insurance_enabled: data?.healthInsuranceEnabled ?? false,
    health_insurance_value: parseFloat(data?.healthInsuranceValue) || 0,
  };
};

export const getOutsourceResources = async () => {
  const { data, error } = await supabase?.from('outsource_resources')?.select('*')?.eq('is_deleted', false)?.order('created_at', { ascending: true });
  if (error) throw error;
  return (data || [])?.map(fromOutsourceResourceRow);
};

export const getDeletedOutsourceResources = async () => {
  const { data, error } = await supabase?.from('outsource_resources')?.select('*')?.eq('is_deleted', true)?.order('deleted_at', { ascending: false });
  if (error) throw error;
  return (data || [])?.map(fromOutsourceResourceRow);
};

export const createOutsourceResource = async (item, userId) => {
  const { data, error } = await supabase?.from('outsource_resources')?.insert(toOutsourceResourceRow(item, userId))?.select()?.single();
  if (error) throw error;
  return fromOutsourceResourceRow(data);
};

export const updateOutsourceResource = async (id, item) => {
  const monthly = parseFloat(item?.monthlySalary) || 0;
  const daily = monthly > 0 ? parseFloat((monthly / WORKING_DAYS_PER_MONTH)?.toFixed(6)) : parseFloat(item?.dailySalary) || 0;
  const hourly = daily > 0 ? parseFloat((daily / WORKING_HOURS_PER_DAY)?.toFixed(6)) : parseFloat(item?.hourlySalary) || 0;
  const nationality = item?.nationality || 'expat';
  const spfPercentage = parseFloat(item?.spfPercentage) || 22.5;
  const spfValue = nationality === 'omani' ? parseFloat((monthly * (spfPercentage / 100))?.toFixed(4)) : 0;
  const { data, error } = await supabase?.from('outsource_resources')?.update({
    name: item?.name,
    resource_name: item?.name,
    role: item?.role || 'mid',
    specialization: item?.specialization,
    role_skill: item?.specialization,
    monthly_salary: monthly,
    daily_salary: daily,
    hourly_salary: hourly,
    internal_cost: daily,
    utilization_default: parseFloat(item?.utilizationDefault) || 85,
    nationality,
    spf_percentage: spfPercentage,
    spf_value: spfValue,
    health_insurance_enabled: item?.healthInsuranceEnabled ?? false,
    health_insurance_value: parseFloat(item?.healthInsuranceValue) || 0,
    updated_at: new Date()?.toISOString(),
  })?.eq('id', id)?.select()?.maybeSingle();
  if (error) throw error;
  if (!data) {
    // Fallback: return the item as-is with computed fields
    return {
      ...item,
      monthlySalary: monthly,
      dailySalary: daily,
      hourlySalary: hourly,
      internalCost: daily,
      nationality,
      spfPercentage,
      spfValue,
      healthInsuranceEnabled: item?.healthInsuranceEnabled ?? false,
      healthInsuranceValue: parseFloat(item?.healthInsuranceValue) || 0,
    };
  }
  return fromOutsourceResourceRow(data);
};

export const deleteOutsourceResource = async (id, userEmail) => {
  const { error } = await supabase?.from('outsource_resources')?.update({
    is_deleted: true,
    deleted_at: new Date()?.toISOString(),
    deleted_by_email: userEmail || null,
  })?.eq('id', id);
  if (error) throw error;
  return true;
};

export const restoreOutsourceResource = async (id) => {
  const { data, error } = await supabase?.from('outsource_resources')?.update({
    is_deleted: false,
    deleted_at: null,
    deleted_by_email: null,
    updated_at: new Date()?.toISOString(),
  })?.eq('id', id)?.select()?.single();
  if (error) throw error;
  return fromOutsourceResourceRow(data);
};

export const permanentlyDeleteOutsourceResource = async (id) => {
  const { error } = await supabase?.from('outsource_resources')?.delete()?.eq('id', id);
  if (error) throw error;
  return true;
};
